<template>
  <fullscreen-dialog v-model="model" :title="dialogTitle" hide-footer icon="mdi-history">
    <v-card flat>
      <v-card-text>
        <!-- User Info Summary -->
        <v-card outlined class="mb-4" v-if="user">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-icon size="48" color="primary" class="mr-3"
                    >mdi-account-circle</v-icon
                  >
                  <div>
                    <div class="text-h6">{{ user.user_name }}</div>
                    <div class="text-caption">ID: {{ user.user_id }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <v-row justify="space-between" align="center">
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("current_balance") }}
                      </div>
                      <div class="text-h6 success--text font-weight-bold">
                        {{ formatCurrency(user.current_credit_balance) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("total_topups") }}
                      </div>
                      <div class="text-h6 primary--text font-weight-bold">
                        {{ topupHistory.length }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("company_topups_total") }}
                      </div>
                      <div class="text-h6 blue--text font-weight-bold">
                        {{ formatCurrency(companyTopupsTotal) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("employee_topups_total") }}
                      </div>
                      <div class="text-h6 grey--text font-weight-bold">
                        {{ formatCurrency(employeeTopupsTotal) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="auto">
                    <primary-button @click="addCredit">
                      <v-icon class="mr-2">mdi-cash-plus</v-icon>
                      {{ $t("add_credit") }}
                    </primary-button>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- History Table -->
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-format-list-bulleted</v-icon>
            {{ $t("topup_history") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="topupHistoryWithTotal"
            hide-default-footer
            disable-pagination
            class="elevation-0"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.num }}
              </span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span
                :class="
                  item.isTotal
                    ? 'font-weight-bold primary--text'
                    : 'font-weight-bold success--text'
                "
              >
                {{ item.isTotal ? "" : "+" }}{{ formatCurrency(item.amount) }}
              </span>
            </template>

            <template v-slot:item.payment_type="{ item }">
              <v-chip
                v-if="!item.isTotal"
                small
                :color="getPaymentTypeColor(item.payment_type)"
              >
                {{ item.payment_type }}
              </v-chip>
            </template>

            <template v-slot:item.topped_by="{ item }">
              <v-chip
                v-if="!item.isTotal"
                small
                :color="item.topped_by === 'Firma' ? 'blue' : 'grey'"
              >
                {{ item.topped_by }}
              </v-chip>
            </template>

            <template v-slot:item.balance_after="{ item }">
              <span v-if="!item.isTotal">
                {{ formatCurrency(item.balance_after) }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "CreditUserHistoryDialog",
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel],
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  computed: {
    dialogTitle() {
      return this.user
        ? `${this.$t("topup_history")} - ${this.user.user_name}`
        : this.$t("topup_history");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("payment_type"), value: "payment_type", sortable: false },
        { text: this.$t("topped_by"), value: "topped_by", sortable: false },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        {
          text: this.$t("balance_after"),
          value: "balance_after",
          sortable: false,
          align: "right",
        },
      ];
    },
    topupHistory() {
      if (!this.user) return [];

      // Generate fake history data
      const paymentTypes = ["Gotovina", "Kartica", "Bankarski prenos"];
      const amounts = [20, 50, 75, 100, 150, 200];
      const toppedByOptions = ["Firma", "Radnik"];
      const history = [];

      // Generate 5-10 random topups
      const count = 5 + Math.floor(Math.random() * 6);

      // Start with current balance and work backwards
      let balance = this.user.current_credit_balance || 100;

      for (let i = 0; i < count; i++) {
        const amount = amounts[Math.floor(Math.random() * amounts.length)];
        const daysAgo = i * (2 + Math.floor(Math.random() * 3));
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);

        history.push({
          id: i + 1,
          date: date.toISOString().substr(0, 10),
          amount: amount,
          payment_type: paymentTypes[Math.floor(Math.random() * paymentTypes.length)],
          topped_by: toppedByOptions[Math.floor(Math.random() * toppedByOptions.length)],
          balance_after: balance,
        });

        // Calculate balance before this topup (always positive)
        // Random amount between 10 and (balance - amount) to ensure next balance_after is positive
        const maxPreviousBalance = Math.max(10, balance - amount);
        const minPreviousBalance = Math.max(10, balance - amount - 50);
        balance =
          minPreviousBalance + Math.random() * (maxPreviousBalance - minPreviousBalance);
      }

      return history;
    },
    topupHistoryWithTotal() {
      const history = this.topupHistory.map((item, index) => ({
        ...item,
        num: index + 1,
        isTotal: false,
      }));

      // Add total row
      if (history.length > 0) {
        const totalAmount = history.reduce((sum, item) => sum + item.amount, 0);
        history.push({
          id: "total",
          num: "",
          date: "",
          amount: totalAmount,
          payment_type: "",
          topped_by: "",
          balance_after: 0,
          isTotal: true,
        });
      }

      return history;
    },
    companyTopupsTotal() {
      return this.topupHistory
        .filter((topup) => topup.topped_by === "Firma")
        .reduce((sum, topup) => sum + topup.amount, 0);
    },
    employeeTopupsTotal() {
      return this.topupHistory
        .filter((topup) => topup.topped_by === "Radnik")
        .reduce((sum, topup) => sum + topup.amount, 0);
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00 " + this.restaurantCurrency;
      return parseFloat(amount).toFixed(2) + " " + this.restaurantCurrency;
    },
    getPaymentTypeColor(type) {
      const colors = {
        Gotovina: "green",
        Kartica: "blue",
        "Bankarski prenos": "orange",
      };
      return colors[type] || "grey";
    },
    addCredit() {
      // Close dialog and emit event to navigate to topup tab
      this.model = false;
      this.$emit("navigate-to-topup", this.user.user_id);
    },
  },
};
</script>
